/**
 * WebSocket Client for Real-time Features
 *
 * This module provides a singleton Socket.io client configured for
 * the UniTree notification and real-time event system.
 *
 * Features:
 * - Automatic reconnection with exponential backoff
 * - Connection state management
 * - Event subscription management
 * - Token-based authentication
 * - Multi-tab support via shared connection
 */

import { io, Socket } from 'socket.io-client';
import { getAccessToken } from '@/lib/api-client';
import type {
  WebSocketEventMap,
  ClientEventMap,
  ServerResponse,
  WebSocketEventName,
} from './events';

// =============================================================================
// CONFIGURATION
// =============================================================================

const SOCKET_URL = import.meta.env.VITE_WS_URL || 'http://localhost:5020';
const NAMESPACE = '/notifications';

export interface SocketConfig {
  url?: string;
  namespace?: string;
  autoConnect?: boolean;
  reconnectionAttempts?: number;
  reconnectionDelay?: number;
  reconnectionDelayMax?: number;
}

const defaultConfig: Required<SocketConfig> = {
  url: SOCKET_URL,
  namespace: NAMESPACE,
  autoConnect: false, // Don't auto-connect - wait for authentication
  reconnectionAttempts: 10,
  reconnectionDelay: 1000,
  reconnectionDelayMax: 30000,
};

// =============================================================================
// CONNECTION STATE
// =============================================================================

export type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'error';

export interface ConnectionInfo {
  state: ConnectionState;
  error?: string;
  reconnectAttempt?: number;
  lastConnected?: Date;
}

// =============================================================================
// SOCKET CLIENT CLASS
// =============================================================================

type EventHandler<T> = (payload: T) => void;
type AnyEventHandler = EventHandler<unknown>;

class SocketClient {
  private socket: Socket | null = null;
  private config: Required<SocketConfig>;
  private connectionInfo: ConnectionInfo = { state: 'disconnected' };
  private eventListeners: Map<string, Set<AnyEventHandler>> = new Map();
  private connectionListeners: Set<(info: ConnectionInfo) => void> = new Set();
  private isAuthenticated = false;
  private authData: { tenantId: string; userId: string; token?: string } | null = null;

  /** Callback invoked when a connection error indicates an auth failure (401/unauthorized) */
  onAuthError: (() => void) | null = null;

  constructor(config: SocketConfig = {}) {
    this.config = { ...defaultConfig, ...config };
  }

  /**
   * Initialize the socket connection
   */
  initialize(): void {
    if (this.socket) {
      return; // Already initialized
    }

    this.socket = io(`${this.config.url}${this.config.namespace}`, {
      autoConnect: this.config.autoConnect,
      reconnection: true,
      reconnectionAttempts: this.config.reconnectionAttempts,
      reconnectionDelay: this.config.reconnectionDelay,
      reconnectionDelayMax: this.config.reconnectionDelayMax,
      transports: ['websocket', 'polling'],
    });

    this.setupEventHandlers();
  }

  /**
   * Set up internal event handlers
   */
  private setupEventHandlers(): void {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      this.updateConnectionState({
        state: 'connected',
        lastConnected: new Date(),
      });

      // Re-authenticate on reconnect with a fresh token
      if (this.authData) {
        const freshToken = getAccessToken();
        this.authenticate({
          ...this.authData,
          token: freshToken ?? undefined,
        });
      }
    });

    this.socket.on('disconnect', (reason: string) => {
      this.updateConnectionState({
        state: 'disconnected',
        error: reason,
      });
      this.isAuthenticated = false;
    });

    this.socket.on('connect_error', (error: Error) => {
      this.updateConnectionState({
        state: 'error',
        error: error.message,
      });

      // Detect auth-related connection errors and invoke the callback
      const msg = error.message.toLowerCase();
      if (msg.includes('auth') || msg.includes('401') || msg.includes('unauthorized')) {
        this.onAuthError?.();
      }
    });

    this.socket.io.on('reconnect_attempt', (attempt: number) => {
      this.updateConnectionState({
        state: 'connecting',
        reconnectAttempt: attempt,
      });
    });

    this.socket.io.on('reconnect', () => {
      this.updateConnectionState({
        state: 'connected',
        lastConnected: new Date(),
      });
    });

    // Set up proxy for all server events
    this.socket.onAny((eventName: string, payload: unknown) => {
      const handlers = this.eventListeners.get(eventName);
      if (handlers) {
        handlers.forEach((handler) => {
          try {
            handler(payload);
          } catch (error) {
            console.error(`[Socket] Error in event handler for ${eventName}:`, error);
          }
        });
      }
    });
  }

  /**
   * Update connection state and notify listeners
   */
  private updateConnectionState(info: Partial<ConnectionInfo>): void {
    this.connectionInfo = { ...this.connectionInfo, ...info };
    this.connectionListeners.forEach((listener) => {
      try {
        listener(this.connectionInfo);
      } catch (error) {
        console.error('[Socket] Error in connection listener:', error);
      }
    });
  }

  /**
   * Connect to the WebSocket server
   */
  connect(): void {
    if (!this.socket) {
      this.initialize();
    }

    if (this.socket && !this.socket.connected) {
      this.updateConnectionState({ state: 'connecting' });
      this.socket.connect();
    }
  }

  /**
   * Disconnect from the server
   */
  disconnect(): void {
    if (this.socket?.connected) {
      this.socket.disconnect();
    }
    this.isAuthenticated = false;
    this.authData = null;
  }

  /**
   * Authenticate with the server
   */
  async authenticate(data: {
    tenantId: string;
    userId: string;
    token?: string;
  }): Promise<ServerResponse> {
    this.authData = data;

    if (!this.socket?.connected) {
      this.connect();
      // Wait for connection
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Connection timeout'));
        }, 10000);

        const onConnect = () => {
          clearTimeout(timeout);
          this.socket?.off('connect', onConnect);
          this.socket?.off('connect_error', onError);
          resolve();
        };

        const onError = (error: Error) => {
          clearTimeout(timeout);
          this.socket?.off('connect', onConnect);
          this.socket?.off('connect_error', onError);
          reject(error);
        };

        this.socket?.on('connect', onConnect);
        this.socket?.on('connect_error', onError);
      });
    }

    return new Promise((resolve, reject) => {
      if (!this.socket) {
        reject(new Error('Socket not initialized'));
        return;
      }

      this.socket.emit('join', data, (response: ServerResponse) => {
        if (response.success) {
          this.isAuthenticated = true;
          resolve(response);
        } else {
          this.isAuthenticated = false;
          reject(new Error(response.message || 'Authentication failed'));
        }
      });
    });
  }

  /**
   * Leave the current session
   */
  leave(): void {
    if (this.socket?.connected) {
      this.socket.emit('leave');
    }
    this.isAuthenticated = false;
    this.authData = null;
  }

  /**
   * Emit an event to the server
   */
  emit<K extends keyof ClientEventMap>(
    event: K,
    data?: ClientEventMap[K],
  ): Promise<ServerResponse> {
    return new Promise((resolve, reject) => {
      if (!this.socket?.connected) {
        reject(new Error('Socket not connected'));
        return;
      }

      if (!this.isAuthenticated && event !== 'join') {
        reject(new Error('Not authenticated'));
        return;
      }

      if (data !== undefined) {
        this.socket.emit(event, data, (response: ServerResponse) => {
          if (response.success) {
            resolve(response);
          } else {
            reject(new Error(response.message || 'Operation failed'));
          }
        });
      } else {
        this.socket.emit(event, (response: ServerResponse) => {
          if (response.success) {
            resolve(response);
          } else {
            reject(new Error(response.message || 'Operation failed'));
          }
        });
      }
    });
  }

  /**
   * Subscribe to a server event
   */
  on<K extends WebSocketEventName>(
    event: K,
    handler: EventHandler<WebSocketEventMap[K]>,
  ): () => void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, new Set());
    }

    const listeners = this.eventListeners.get(event);
    if (!listeners) {
      throw new Error(`WebSocket listener set was not initialized for ${event}`);
    }

    listeners.add(handler);

    // Return unsubscribe function
    return () => {
      this.eventListeners.get(event)?.delete(handler);
    };
  }

  /**
   * Unsubscribe from a server event
   */
  off<K extends WebSocketEventName>(event: K, handler?: EventHandler<WebSocketEventMap[K]>): void {
    if (handler) {
      this.eventListeners.get(event)?.delete(handler);
    } else {
      this.eventListeners.delete(event);
    }
  }

  /**
   * Subscribe to connection state changes
   */
  onConnectionChange(handler: (info: ConnectionInfo) => void): () => void {
    this.connectionListeners.add(handler);
    // Immediately call with current state
    handler(this.connectionInfo);

    return () => {
      this.connectionListeners.delete(handler);
    };
  }

  /**
   * Get current connection state
   */
  getConnectionInfo(): ConnectionInfo {
    return { ...this.connectionInfo };
  }

  /**
   * Check if connected
   */
  get isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  /**
   * Check if authenticated
   */
  get authenticated(): boolean {
    return this.isAuthenticated;
  }
}

// =============================================================================
// SINGLETON INSTANCE
// =============================================================================

export const socketClient = new SocketClient();

// Export class for testing or custom instances
export { SocketClient };
